import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { SpeakerListResponse } from '@utils/types/organization/speaker.ts';

interface SearchSpeakersParams {
  search?: string;
  name?: string;
  role?: string;
  party?: string;
  limit?: number;
}

// Backend `list_speakers` only honors search through the `q` query param using
// the `field:op:value` grammar (see api-scriptix dependencies/search.py). Sending
// `name=`/`role=`/`party=` as separate params is silently ignored, which caused
// the picker to only ever see the first `limit` alphabetical speakers.
//
// We build the URL manually so we can pass multiple `q=` params (one per field),
// since `buildCollectionUrl` flattens an array `q` into a comma-joined string.
export const useSearchSpeakers = (params: SearchSpeakersParams = {}) => {
  const { search, name, role, party, limit = 50 } = params;

  const fetch = async (): Promise<SpeakerListResponse> => {
    const basePath = GET_URL().speakers;
    const qs = new URLSearchParams();
    qs.set('offset', '0');
    qs.set('limit', String(limit));
    qs.set('sort', 'name');
    qs.set('direction', 'asc');

    const trimmedSearch = search?.trim();
    const trimmedName = name?.trim();
    const trimmedRole = role?.trim();
    const trimmedParty = party?.trim();

    if (trimmedSearch) {
      qs.append('q', `name:ilike:%${trimmedSearch}%`);
    }
    if (trimmedName) {
      qs.append('q', `name:ilike:%${trimmedName}%`);
    }
    if (trimmedRole) {
      qs.append('q', `role:ilike:%${trimmedRole}%`);
    }
    if (trimmedParty) {
      qs.append('q', `party:ilike:%${trimmedParty}%`);
    }

    const { data } = await Axios.get<SpeakerListResponse>(
      `${basePath}?${qs.toString()}`
    );
    return data;
  };

  return useQuery(
    ['speakers-search', search, name, role, party, limit],
    fetch,
    { staleTime: 30 * 1000 }
  );
};
